import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTransition } from "react";
import { useTranslations } from "use-intl";
import { Download, Link2, Loader2, Share2, Unlink } from "lucide-react";
import { toast } from "sonner";
import { CopyButton } from "@/components/app/copy-button";
import { Button } from "@/components/ui/button";
import { Drawer, DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";
import { Input } from "@/components/ui/input";
import { api, call } from "@/lib/api";
import { qk } from "@/lib/queries";

/** Share link for users of this server, and the plan as a file. */
export function SharePlanDrawer({
  planId,
  planName,
  open,
  onOpenChange,
}: {
  planId: string;
  planName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const t = useTranslations("share");
  const tc = useTranslations("common");
  const qc = useQueryClient();
  const share = useQuery({
    queryKey: qk.planShare(planId),
    queryFn: () => call(api.plans[":id"].share.$get({ param: { id: planId } })),
    enabled: open,
  });
  const [pending, start] = useTransition();
  const token = share.data?.token ?? null;
  const url = token ? `${window.location.origin}/shared/${token}` : null;

  const run = (fn: () => Promise<{ token: string | null }>) =>
    start(async () => {
      try {
        qc.setQueryData(qk.planShare(planId), await fn());
      } catch {
        toast.error(tc("error"));
      }
    });

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent>
        <DrawerHeader className="text-left">
          <DrawerTitle>{t("title")}</DrawerTitle>
          <DrawerDescription>{t("intro")}</DrawerDescription>
        </DrawerHeader>
        <div className="space-y-6 px-4 pb-8">
          <section>
            <h3 className="flex items-center gap-2 font-semibold">
              <Link2 className="size-4 text-primary" /> {t("linkTitle")}
            </h3>
            <p className="mt-1 text-sm text-muted-foreground">{t("linkText")}</p>
            {share.isPending ? (
              <Loader2 className="mt-3 animate-spin text-muted-foreground" />
            ) : url ? (
              <div className="mt-3 space-y-2">
                <Input readOnly value={url} aria-label={t("linkTitle")} onFocus={(e) => e.currentTarget.select()} className="h-11 text-sm" />
                <div className="flex flex-wrap gap-2">
                  <CopyButton value={url} />
                  {"share" in navigator && (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => navigator.share({ title: planName, text: t("shareText", { name: planName }), url }).catch(() => {})}
                    >
                      <Share2 /> {t("shareVia")}
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-destructive"
                    disabled={pending}
                    onClick={() =>
                      run(async () => {
                        await call(api.plans[":id"].share.$delete({ param: { id: planId } }));
                        toast.success(t("stopped"));
                        return { token: null };
                      })
                    }
                  >
                    <Unlink /> {t("stop")}
                  </Button>
                </div>
              </div>
            ) : (
              <Button
                className="mt-3"
                disabled={pending}
                onClick={() => run(() => call(api.plans[":id"].share.$post({ param: { id: planId } })))}
              >
                {pending ? <Loader2 className="animate-spin" /> : <Link2 />} {t("create")}
              </Button>
            )}
          </section>
          <section>
            <h3 className="flex items-center gap-2 font-semibold">
              <Download className="size-4 text-primary" /> {t("fileTitle")}
            </h3>
            <p className="mt-1 text-sm text-muted-foreground">{t("fileText")}</p>
            <Button asChild variant="outline" className="mt-3">
              <a href={`/api/plans/${planId}/export`} download>
                <Download /> {t("download")}
              </a>
            </Button>
          </section>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
